import { Card } from "@app/ui";
import { useLayoutEffect, useRef, useState } from "react";

import { useMobileUx } from "../../../lib/useMobileUx.js";
import type { BoardTargetRef } from "../../../stores/index.js";
import { SettingsAnchor } from "../SettingsCard.js";
import { PaletteBoardPanel } from "./PaletteBoardPanel.js";
import { ThemePreviewPanel } from "./ThemePreviewPanel.js";

/**
 * The palette board with the live calendar preview pinned at its side on
 * desktop — shared by Settings → UI Colors and Settings → Topics, Labels and
 * Calendars so the two pages match exactly. The preview's height is measured
 * off the board (`ResizeObserver`, since `Card` doesn't forward refs — the ref
 * lives on the wrapping div, see `SettingsCard.tsx`) and applied to the
 * preview `Card` directly: a CSS-only `items-stretch` row would instead size
 * to whichever side's UNCONSTRAINED content is taller, which can inflate the
 * row past the board's natural height when the mock day has many topics.
 */
export function PaletteBoardWithPreview({
  boardAnchorId,
  previewAnchorId,
  defaultTarget,
}: {
  boardAnchorId: string;
  previewAnchorId: string;
  defaultTarget?: BoardTargetRef | undefined;
}) {
  const mobile = useMobileUx();
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [boardHeight, setBoardHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (mobile) return undefined;
    const el = boardRef.current;
    if (!el) return undefined;
    const measure = () => setBoardHeight(Math.round(el.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // The board's canvas artwork loads asynchronously (`PalettePicker.load()`),
    // growing the board slightly after first paint. ResizeObserver normally
    // catches that follow-up resize, but its callback timing is tied to the
    // browser's rendering cadence, which can lag well behind mount for a
    // backgrounded/inactive tab — a few cheap re-measures shortly after mount
    // keep the preview in sync even then.
    const timers = [100, 300, 800].map((ms) => setTimeout(measure, ms));
    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [mobile]);

  return (
    <div className={mobile ? undefined : "flex items-start gap-4"}>
      <div ref={boardRef} className="min-w-0 flex-1">
        <SettingsAnchor id={boardAnchorId}>
          <Card className="p-4">
            <PaletteBoardPanel defaultTarget={defaultTarget} />
          </Card>
        </SettingsAnchor>
      </div>
      {!mobile && (
        <div className="w-72 shrink-0">
          <SettingsAnchor id={previewAnchorId}>
            <ThemePreviewPanel height={boardHeight ?? undefined} />
          </SettingsAnchor>
        </div>
      )}
    </div>
  );
}
